import {fade} from '@material-ui/core/styles/colorManipulator';
import {primaryBackground, secondaryBackground, dangerBackground, warningBackground} from '@zegal/components/src/components/src/assets/global.style';

const buttonStyle = (theme) => {
	return {
		button: {
			minHeight: 'auto',
			minWidth: 100,
			backgroundColor: theme.color.dl_lightgrey,
			color: theme.color.dl_white,
			boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, .3)',
			border: 'none',
			borderRadius: 3,
			position: 'relative',
			padding: `${theme.typography.pxToRem(10)} ${theme.typography.pxToRem(20)}`,
			margin: `${theme.typography.pxToRem(5)} ${theme.typography.pxToRem(1)}`,
			fontSize: theme.font.regular,
			fontWeight: theme.font.semiBold,
			textTransform: 'none',
			letterSpacing: 0,
			lineHeight: '1.3',
			textAlign: 'center',
			transition: 'none',

			'&:hover,&:focus': {
				backgroundColor: theme.color.dl_lightgrey,
				color: theme.color.dl_white
			},

			// transitioning label
			'& label, & .button-label': {
				cursor: 'inherit',

				'& > *:last-child:not(strong)': {
					alignSelf: 'baseline'
				}
			},

			// if button has spinner
			'&.has-spinner': {
				// spinner wrapper
				'& *[data-zegal-spinner="__ZEGAL__LOADING__SPINNER__"]': {
					'& > *': {
						margin: 0
					}
				},

				// spinner button label
				'& label': {
					transform: 'translateX(8px)'
				}
			}
		},

		align: {
			'& label, & .button-label': {
				justifyContent: 'flex-start'
			}
		},

		// state
		disabled: {
			backgroundColor: theme.color.default.disabled,
			boxShadow: 'none',
			pointerEvents: 'none'
		},

		active: {
			...primaryBackground(theme)
		},

		upperCase: {
			textTransform: 'uppercase',

			'& *': {
				textTransform: 'inherit'
			}
		},

		// color
		primary: {
			...primaryBackground(theme)
		},

		secondary: {
			...secondaryBackground(theme)
		},

		danger: {
			...dangerBackground(theme)
		},

		warning: {
			...warningBackground(theme),
			color: theme.color.dl_white + ' !important'

		},

		white: {
			'&, &:focus, &:hover, &:visited': {
				backgroundColor: theme.color.dl_white,
				color: theme.palette.text.primary
			}
		},

		black: {
			'&, &:focus, &:hover, &:visited': {
				backgroundColor: theme.color.dl_darkslate,
				color: theme.color.dl_white
			}
		},

		transparent: {
			'&, &:focus, &:hover': {
				background: 'transparent',
				boxShadow: 'none',
				color: 'inherit'
			}
		},

		// size
		xl: {
			padding: `${theme.typography.pxToRem(14)} ${theme.typography.pxToRem(24)}`,
			borderRadius: 3
		},

		lg: {
			padding: `${theme.typography.pxToRem(10)} ${theme.typography.pxToRem(20)}`,
			borderRadius: 3
		},

		sm: {
			margin: `${theme.typography.pxToRem(1)}`,
			padding: `${theme.typography.pxToRem(5)} ${theme.typography.pxToRem(10)}`,
			fontSize: theme.font.small,
			borderRadius: 3,
			minWidth: 'unset'
		},

		md: {
			padding: `${theme.typography.pxToRem(8)} ${theme.typography.pxToRem(10)}`,
			fontSize: theme.font.small,
			fontWeight: theme.font.semiBold,
			borderRadius: 0,
			boxShadow: 'none !important',
			marginRight: 12,

			'&:last-child': {
				marginRight: 0
			}
		},

		xs: {
			minWidth: 64,
			margin: `${theme.typography.pxToRem(0)} ${theme.typography.pxToRem(1)}`,
			padding: `${theme.typography.pxToRem(4)} ${theme.typography.pxToRem(8)}`,
			fontSize: theme.font.tiny,
			borderRadius: 2,
			fontWeight: theme.font.semiBold,
		},

		auto: {
			height: 'auto',
			width: 'auto',
			padding: `${theme.typography.pxToRem(2)} ${theme.typography.pxToRem(4)}`,
			minWidth: 'unset',
			fontSize: 'inherit'
		},

		// link
		link: {
			'&, &:focus, &:hover, &:visited': {
				padding: 0,
				backgroundColor: 'transparent',
				boxShadow: 'none',
				display: 'inline-flex',
				color: 'inherit',
				minWidth: 'unset',
				fontSize: 'inherit'
			},

			'& .material-icons': {
				marginRight: 6
			},

			'&$disabled': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.color.default.disabled + ' !important'
				}
			},

			'&$primary': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.palette.primary.main
				}
			},

			'&$secondary': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.palette.secondary.main
				}
			},

			'&$danger': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.palette.error.main
				}
			},

			'&$black': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.color.dl_darkslate
				}
			}
		},

		withIcon: {
			display: 'inline-flex',

			'& .material-icons': {
				margin: '0 6px 0 0'
			},

			'&$xs': {
				'& .material-icons': {
					fontSize: 16,
					margin: '0 3px 0 0'
				},

				'& svg': {
					width: 16,
					height: 16
				}
			},

			'&$sm': {
				'& .material-icons': {
					fontSize: 20
				}
			},

			'&$lg': {
				'& .material-icons': {
					fontSize: 52
				}
			},

			'&$auto': {
				height: 'auto',
				width: 'auto'
			}
		},

		justIcon: {
			height: 36,
			width: 36,
			margin: 0,
			borderRadius: '50%',
			padding: 0,
			minWidth: 'unset',

			'& .material-icons': {
				margin: 0,
				fontSize: 22
			},

			[theme.breakpoints.up('md')]: {
				height: 40,
				width: 40
			},

			'&$xs': {
				height: 20,
				width: 20,

				'& .material-icons': {
					fontSize: 16
				}
			},

			'&$sm': {
				height: 30,
				width: 30,

				'& .material-icons': {
					fontSize: 18
				}
			},

			'&$lg': {
				height: 46,
				width: 46,

				'& .material-icons': {
					fontSize: 24
				}
			},

			'&$xl': {
				height: 54,
				width: 54,

				'& .material-icons': {
					fontSize: 44
				}
			},

			'&$auto': {
				height: 'auto',
				width: 'auto'
			}
		},

		// opacity
		opacity: {
			'&$disabled': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.color.dl_dirtgrey + ' !important'
				}
			},

			'&$primary': {
				'&, &:focus, &:hover, &:visited': {
					backgroundColor: theme.palette.primary.light,
					color: theme.palette.primary.main
				}
			},

			'&$secondary': {
				'&, &:focus, &:hover, &:visited': {
					backgroundColor: theme.palette.secondary.light,
					color: theme.palette.secondary.contrastText
				}
			},

			'&$danger': {
				'&, &:focus, &:hover, &:visited': {
					backgroundColor: theme.palette.error.light,
					color: theme.color.default.error
				}
			},

			'&$warning': {
				'&, &:focus, &:hover, &:visited': {
					backgroundColor: fade(theme.color.default.warning, .8),
					color: theme.color.default.warning + ' !important'
				}
			},

			'&$black': {
				'&, &:focus, &:hover, &:visited': {
					backgroundColor: fade(theme.color.dl_darkslate, .8),
					color: theme.color.dl_darkslate
				}
			}
		},

		// simple
		simple: {
			backgroundColor: 'transparent',
			color: theme.palette.text.secondary,
			boxShadow: 'none',

			'&:focus, &:hover, &:visited': {
				backgroundColor: 'transparent',
				color: theme.palette.text.secondary
			},

			'&$disabled': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.color.default.disabled + ' !important'
				}
			},

			'&$primary': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.palette.primary.main
				}
			},

			'&$secondary': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.palette.secondary.main
				}
			},

			'&$danger': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.palette.error.main
				}
			},

			'&$warning': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.color.default.warning + ' !important'
				}
			},

			'&$white': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.color.dl_white
				}
			},

			'&$black': {
				'&, &:focus, &:hover, &:visited': {
					color: theme.color.dl_darkslate
				}
			},

			'&$transparent': {
				'&, &:focus, &:hover, &:visited': {
					color: 'inherit'
				}
			},

			// if justIcon with simple variant
			'&$justIcon, &$withIcon': {
				'&:hover, &:focus, &:visited': {
					backgroundColor: 'rgba(0, 0, 0, 0.04) !important'
				},

				'&$auto': {
					backgroundColor: 'transparent !important'
				},

				'&$primary': {
					'&:hover, &:focus, &:visited': {
						backgroundColor: theme.palette.primary.light + ' !important'
					}
				}
			}
		},

		// border
		border: {
			'&, &:focus, &:hover, &:visited': {
				backgroundColor: theme.color.dl_white,
				color: theme.palette.text.secondary,
				border: '1px solid' + theme.palette.divider,
				/*
				 * Adding small vertical offset shadow
				 * for to overcome cross-browser cutting
				 * border line issue.
				 */
				boxShadow: 'none'
			},

			'&$simple': {
				'&, &:focus, &:hover, &:visited': {
					backgroundColor: 'transparent'
				}
			},

			'&$disabled': {
				'&, &:focus, &:hover, &:visited': {
					border: '1px solid ' + theme.color.default.disabled,
					color: theme.color.default.disabled + ' !important'
				}
			},

			'&$active': {
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.primary.contrastText
			},

			'&$primary': {
				'&, &:focus, &:hover, &:visited': {
					border: '1px solid ' + theme.color.default.border_primary,
					color: theme.palette.primary.main
				}
			},

			'&$secondary': {
				'&, &:focus, &:hover, &:visited': {
					border: '1px solid ' + theme.color.default.border_secondary,
					color: theme.palette.secondary.main
				}
			},

			'&$danger': {
				'&, &:focus, &:hover, &:visited': {
					border: '1px solid ' + theme.palette.error.main + ' !important',
					color: theme.palette.error.main
				}
			},

			'&$warning': {
				'&, &:focus, &:hover, &:visited': {
					border: '1px solid ' + theme.color.default.warning + ' !important',
					color: theme.color.default.warning + ' !important'
				}
			},

			'&$black': {
				'&, &:focus, &:hover, &:visited': {
					border: '1px solid ' + theme.color.dl_darkslate + ' !important',
					color: theme.color.dl_darkslate
				}
			},

			'&$white': {
				'&, &:focus, &:hover, &:visited': {
					background: 'transparent',
					border: '1px solid ' + theme.color.dl_white + ' !important',
					color: theme.color.dl_white
				}
			},

			'&$transparent': {
				'&, &:focus, &:hover, &:visited': {
					border: 'none !important'
				}
			},

			'&$justIcon': {
				borderRadius: 4
			}
		},

		// noShadow
		noShadow: {
			boxShadow: 'none'
		},

		// simple
		fab: {
			display: 'flex',
			alignItems: 'center',
			width: 'auto',
			height: 40,
			padding: '0 16px',
			minWidth: 40,
			borderRadius: 20,
			fontSize: 'inherit',

			'&$sm': {
				height: 28,
				minWidth: 28,
				padding: '0 7px 0 12px',
				borderRadius: 17
			},

			'&$lg': {
				height: 61.5,
				minWidth: 61.5,
				borderRadius: '50%',

				'& .material-icons': {
					fontWeight: theme.font.bold
				}
			}
		}
	};
};

export default buttonStyle;
